export default () => ({
  initScrollyAnimation() {
    // Helper: returns tween configuration for fadeIn/slideIn (and default) animations.
    const getAnimationConfig = (animType, offset, duration, ease, direction) => {
      let fromVars, toVars;
      switch (animType) {
        case 'fadeIn':
          fromVars = { autoAlpha: 0, y: offset };
          toVars   = { autoAlpha: 1, y: 0, duration, ease };
          break;
        case 'slideIn':
          fromVars = { autoAlpha: 0 };
          if (direction === 'right') {
            fromVars.x = -offset;
          } else if (direction === 'left') {
            fromVars.x = offset;
          } else if (direction === 'down') {
            fromVars.y = -offset;
          } else if (direction === 'up') {
            fromVars.y = offset;
          } else {
            fromVars.x = -offset;
          }
          toVars = { autoAlpha: 1, x: 0, y: 0, duration, ease };
          break;
        default:
          fromVars = { autoAlpha: 0, y: offset };
          toVars   = { autoAlpha: 1, y: 0, duration, ease };
      }
      return { fromVars, toVars };
    };

    const getStartEnd = (el) => {
      const baseStart = el.dataset.start || 'top center';
      const baseEnd   = el.dataset.end   || 'bottom center';

      const hasMobileOverride =
        el.dataset.startMobile !== undefined ||
        el.dataset.endMobile   !== undefined;

      if (!hasMobileOverride) {
        return { start: baseStart, end: baseEnd };
      }

      const isMobile = window.matchMedia('(max-width: 767.98px)').matches;

      if (!isMobile) {
        // Desktop: always use the base values
        return { start: baseStart, end: baseEnd };
      }

      // Mobile: fall back to base if a mobile-specific attribute is missing
      const mobileStart = el.dataset.startMobile || baseStart;
      const mobileEnd   = el.dataset.endMobile   || baseEnd;

      return { start: mobileStart, end: mobileEnd };
    };

    // Loop through all elements with a data-animation attribute.
    gsap.utils.toArray('[data-animation]').forEach(el => {
      // Check if the element should disable animation.
      const disableAnim = el.dataset.disableAnimation;
      if (disableAnim) {
        let shouldDisable = false;
        if (disableAnim.trim().startsWith('(')) {
          shouldDisable = window.matchMedia(disableAnim).matches;
        } else {
          const threshold = parseInt(disableAnim, 10);
          if (!isNaN(threshold)) {
            shouldDisable = window.innerWidth <= threshold;
          }
        }
        if (shouldDisable) return;
      }

      // Retrieve animation type from data attributes.
      const animType = el.dataset.animation;

      // Manual override for trigger type:
      // If data-trigger is set, use that; otherwise, default.
      let triggerType = el.dataset.trigger;
      if (!triggerType) {
        // Always use scroll for parallax and state animations.
        if (animType === 'parallax' || animType === 'state') {
          triggerType = 'scroll';
        } else {
          // For everything else, default to scroll.
          // ScrollTrigger will still fire immediately for elements already in view.
          triggerType = 'scroll';
        }
      }

      // NEW: class/state toggle animation (before/after styles).
      if (animType === 'state') {
        const beforeClasses = (el.dataset.stateBefore || '').split(/\s+/).filter(Boolean);
        const afterClasses  = (el.dataset.stateAfter  || '').split(/\s+/).filter(Boolean);

        const setState = (isActive) => {
          if (isActive) {
            beforeClasses.forEach(cls => el.classList.remove(cls));
            afterClasses.forEach(cls => el.classList.add(cls));
          } else {
            afterClasses.forEach(cls => el.classList.remove(cls));
            beforeClasses.forEach(cls => el.classList.add(cls));
          }
        };

        // Initial state
        setState(false);

        const { start, end } = getStartEnd(el);

        ScrollTrigger.create({
          trigger: el,
          start,
          end,
          onEnter:     () => setState(true),
          onEnterBack: () => setState(true),
          onLeave:     () => setState(false),
          onLeaveBack: () => setState(false),
        });

        return;
      }

      // Custom handling for parallax animations.
      if (animType === 'parallax') {
        const speed       = parseFloat(el.dataset.speed) || 0.1;
        const rotation    = el.dataset.rotation ? parseFloat(el.dataset.rotation) : 0;
        const rotationEnd = el.dataset.rotationEnd ? parseFloat(el.dataset.rotationEnd) : 0;
        const initialY    = -(window.innerHeight * speed);

        gsap.fromTo(
          el,
          { y: initialY, rotation },
          {
            y: 0,
            rotation: rotationEnd,
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: el.dataset.start || 'top bottom',
              end:   el.dataset.end   || 'bottom top',
              scrub: true,
            },
          }
        );

        return;
      }

      // For all other animation types (fadeIn, slideIn, default).
      const offset    = parseFloat(el.dataset.offset) || 50;
      const duration  = el.dataset.duration ? parseFloat(el.dataset.duration) : 1;
      const ease      = el.dataset.easing || 'power1.out';
      const direction = el.dataset.direction || 'left';

      const config = getAnimationConfig(animType, offset, duration, ease, direction);

      // Optional delay ONLY for explicit load-triggered animations.
      const loadDelay = el.dataset.delay ? parseFloat(el.dataset.delay) : 0;
      if (triggerType === 'load' && !isNaN(loadDelay) && loadDelay > 0) {
        config.toVars.delay = loadDelay;
      }

      // Explicit LOAD: only if you set data-trigger="load"
      if (triggerType === 'load') {
        gsap.fromTo(el, config.fromVars, config.toVars);
        return;
      }

      // Default SCROLL behavior (including fadeIn)
      if (triggerType === 'scroll') {
        const start = el.dataset.start || 'top bottom';
        const end   = el.dataset.end   || 'top center';
        const scrub = (el.dataset.lag !== undefined && el.dataset.lag.trim() !== '') 
          ? parseFloat(el.dataset.lag) 
          : true; // your previous default

        config.toVars.scrollTrigger = {
          trigger: el,
          start,
          end,
          scrub,
        };

        gsap.fromTo(el, config.fromVars, config.toVars);
      }
    });
  },
  // Alpine automatically calls init() on component initialization.
  init() {
    this.initScrollyAnimation();
  }
});